import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import ClassNames from 'embla-carousel-class-names';EmblaCarousel
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import Player from '@vimeo/player';

const youTubePlayers = new Map();
const youTubePlayerStates = new Map();
const vimeoPlayers = new Map();

const updateTabPadding = (emblaNode) => {
  const tabContainer = emblaNode.closest('.tabs__content');
  if (tabContainer) {
    tabContainer.style.padding = window.matchMedia('(min-width: 1024px)').matches
      ? '0 20px'
      : '';
  }
};

const initVideoPlayers = (slide) => {
  const iframe = slide.querySelector('.embed-player');
  if (!iframe) return;

  const videoType = iframe.dataset.type;
  const videoId = iframe.src;

  if (videoType === 'youtube' && !youTubePlayers.has(videoId)) {
    const player = new YT.Player(iframe, {
      events: {
        onReady: () => {
          youTubePlayerStates.set(videoId, true); // mark as ready
        },
      },
    });

    youTubePlayers.set(videoId, player);
    youTubePlayerStates.set(videoId, false); // not ready yet
  }

  if (videoType === 'vimeo' && !vimeoPlayers.has(videoId)) {
    const player = new Player(iframe);
    vimeoPlayers.set(videoId, player);
  }
};

const controlVideoPlayback = (embla) => {
  const slides = embla.slideNodes();
  const activeIndex = embla.selectedScrollSnap();

  slides.forEach((slide, index) => {
    const iframe = slide.querySelector('.embed-player');
    if (!iframe) return;

    const videoType = iframe.dataset.type;
    const videoId = iframe.src;

    slide.setAttribute('aria-hidden', index !== activeIndex);

    const isActive = index === activeIndex;

    if (videoType === 'youtube') {
      const player = youTubePlayers.get(videoId);
      const isReady = youTubePlayerStates.get(videoId);
      if (player && isReady) {
        isActive ? player.playVideo() : player.pauseVideo();
      }
    }

    if (videoType === 'vimeo') {
      const player = vimeoPlayers.get(videoId);
      if (player) {
        isActive ? player.play() : player.pause();
      }
    }
  });
};

let currentIsMobile = window.matchMedia('(max-width: 767px)').matches;

export const initEmblaCarousels = (root = document) => {
  const emblaWrappers = root.querySelectorAll('.carousel-wrapper');

  emblaWrappers.forEach((emblaNode) => {
    const viewportNode = emblaNode.querySelector('.carousel-wrapper__viewport');
    if (!viewportNode) return;

    // Destroy existing Embla if already initialized
    if (emblaNode.dataset.emblaInstance) {
      const existingEmbla = JSON.parse(emblaNode.dataset.emblaInstance);
      if (existingEmbla && typeof existingEmbla.destroy === 'function') {
        existingEmbla.destroy();
      }
    }

    emblaNode.dataset.emblaInitialized = 'true';
    emblaNode.closest('.supapress')?.style.setProperty('overflow', 'visible');

    updateTabPadding(emblaNode);
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    mediaQuery.addEventListener?.('change', () => updateTabPadding(emblaNode));

    let settingsAttr = emblaNode.getAttribute('data-slider-settings');

    if( emblaNode.parentElement.getAttribute('data-carousel-settings') ) {
        settingsAttr = emblaNode.parentElement.getAttribute('data-carousel-settings');
    }

    let slickSettings = {};

    try {
      slickSettings = settingsAttr ? JSON.parse(settingsAttr) : {};
    } catch (error) {
      console.error('Failed to parse slider settings', error);
    }

    // Determine if it's mobile
    const isMobile = window.matchMedia('(max-width: 767px)').matches;

    const {
      infinite = true,
      autoplay = false,
      autoplaySpeed = 8000,
      slidesToScroll: baseSlidesToScroll = 1,
      adaptiveHeight = false,
      speed,
    } = slickSettings;

    const slidesToScroll = isMobile ? 1 : parseInt(baseSlidesToScroll);
    const transitionSpeed = speed ? Math.round(speed / 16.67) : 36;

    const classNames = ClassNames({
      snapped: "is-snapped",
      inView: "is-in-view",
      draggable: "is-draggable",
      dragging: "is-dragging",
      loop: "is-loop",
    });
    const basePlugins = [WheelGesturesPlugin(), classNames];
    const plugins = autoplay
      ? [
          Autoplay({ delay: autoplaySpeed, stopOnInteraction: false }),
          ...basePlugins,
        ]
      : basePlugins;

    const embla = EmblaCarousel(
      viewportNode,
      {
        loop: infinite,
        align: "start",
        speed: transitionSpeed,
        slidesToScroll,
        containScroll: "trimSnaps",
        skipSnaps: true,
      },
      plugins
    );

    emblaNode.dataset.emblaInstance = JSON.stringify(embla); // So we can destroy it later

    const prevBtn = emblaNode.querySelector(".slick-prev");
    const nextBtn = emblaNode.querySelector(".slick-next");
    const dotsNode = emblaNode.querySelector(".slick-dots");

    const updateDots = () => {
      if (!dotsNode) return;
      const selectedIndex = embla.selectedScrollSnap();
      const dots = dotsNode.querySelectorAll(".slick-dot");

      dots.forEach((dot, index) => {
        dot.classList.toggle("slick-active", index === selectedIndex);
      });
    };

    const createDots = () => {
      if (!dotsNode) return;
      const slideCount = embla.scrollSnapList().length;
      dotsNode.innerHTML = "";

      Array.from({ length: slideCount }).forEach((_, i) => {
        const li = document.createElement("li");
        li.classList.add("slick-dot");
        li.setAttribute("role", "presentation");
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("aria-label", `Go to slide ${i + 1}`);
        button.setAttribute("role", "tab");
        button.addEventListener("click", () => embla.scrollTo(i));
        li.appendChild(button);
        dotsNode.appendChild(li);
      });

      updateDots();
    };

    const updateArrows = () => {
      const scrollSnaps = embla.scrollSnapList().length;

      if (!infinite || scrollSnaps >= 1) {
        const canScrollPrev = embla.canScrollPrev();
        const canScrollNext = embla.canScrollNext();

        prevBtn?.classList.toggle("slick-disabled", !canScrollPrev);
        prevBtn?.setAttribute("aria-disabled", String(!canScrollPrev));
        nextBtn?.classList.toggle("slick-disabled", !canScrollNext);
        nextBtn?.setAttribute("aria-disabled", String(!canScrollNext));
      }
    };

    let resizeObserver;

    const updateHeight = () => {
      if (!adaptiveHeight) return;

      const selectedSlide = embla.slideNodes()[embla.selectedScrollSnap()];
      if (!selectedSlide) return;

      // Disconnect any previous observer
      if (resizeObserver) resizeObserver.disconnect();

      resizeObserver = new ResizeObserver(() => {
        const height = selectedSlide.getBoundingClientRect().height;
        viewportNode.style.height = `${height}px`;
      });

      resizeObserver.observe(selectedSlide);
    };

    prevBtn?.addEventListener('click', () => embla.scrollPrev());
    nextBtn?.addEventListener('click', () => embla.scrollNext());

    embla.slideNodes().forEach(initVideoPlayers);

    embla.on('select', () => {
      updateDots();
      updateArrows();
      updateHeight();
      controlVideoPlayback(embla);
    });

    createDots();
    updateArrows();
    updateHeight();
    controlVideoPlayback(embla);
    requestAnimationFrame(() => {
      emblaNode.classList.add('is-ready');
    });
  });
};

const mobileQuery = window.matchMedia('(max-width: 767px)');
mobileQuery.addEventListener('change', (e) => {
  if (e.matches !== currentIsMobile) {
    currentIsMobile = e.matches;
    document.querySelectorAll('.carousel-wrapper').forEach(wrapper => {
      wrapper.removeAttribute('data-embla-initialized');
    });
    initEmblaCarousels();
  }
});


document.addEventListener('DOMContentLoaded', () => {
  const hasYouTubeVideos = !!document.querySelector('.embed-player[data-type="youtube"]');
  const init = () => initEmblaCarousels();

  if (hasYouTubeVideos) {
    if (window.YT && typeof YT.Player === 'function') {
      init();
    } else {
      // Load YouTube API if not already loaded
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = init;
    }
  } else {
    init();
  }
});

document.addEventListener('lazyloaded', () => {
  const emblaWrappers = document.querySelectorAll('.carousel-wrapper');
  if(emblaWrappers.length <= 0) {
    return;
  }
  window.dispatchEvent(new Event('resize'));
});
